import React, { useState, useRef } from 'react';

function ResumeUploadStep({ data, updateData, nextStep, skipStep, isOptional }) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = (files) => {
    const file = files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError(null);
    
    // Store file in localStorage for later upload (since we can't send files through Stripe metadata)
    const reader = new FileReader();
    reader.onload = function(e) {
      const base64Data = e.target.result.split(',')[1]; // Remove data:mime/type;base64, prefix
      const fileData = {
        fileName: file.name,
        fileType: file.type,
        fileData: base64Data
      };
      localStorage.setItem('onboarding_resume_file', JSON.stringify(fileData));
      console.log('📄 Resume stored in localStorage for later upload:', file.name);
    };
    reader.readAsDataURL(file);
    
    updateData({ resumeFile: file });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = () => {
    updateData({ resumeFile: null });
    localStorage.removeItem('onboarding_resume_file');
    console.log('📄 Resume removed from localStorage');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileIcon = (file) => {
    if (file.type === 'application/pdf') {
      return '📄';
    }
    return '📝';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 p-8 md:p-12">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-3">
          Upload your <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">resume</span>
        </h2>
        <p className="text-gray-400">
          Let Elena analyze your resume to provide better job recommendations
        </p>
        {isOptional && (
          <div className="inline-flex items-center mt-3 px-3 py-1 bg-gray-700 bg-opacity-50 rounded-full">
            <span className="text-gray-300 text-sm">Optional - you can skip this step</span>
          </div>
        )}
      </div>

      <div className="max-w-lg mx-auto">
        {/* File Upload Area */}
        {!data.resumeFile ? (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
              dragActive
                ? 'border-blue-500 bg-blue-500 bg-opacity-10'
                : 'border-gray-600 hover:border-gray-500'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            
            <h3 className="text-lg font-medium text-white mb-2">
              Drop your resume here
            </h3>
            <p className="text-gray-400 mb-4">
              Or click to browse your files
            </p>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-200"
            >
              Choose File
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileInput}
              accept=".pdf,.doc,.docx"
              className="hidden"
            />
            
            <p className="text-gray-500 text-xs mt-4">
              Supports PDF, DOC, DOCX files up to 5MB
            </p>
          </div>
        ) : (
          /* File Preview */
          <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-2xl mr-3">{getFileIcon(data.resumeFile)}</span>
                <div>
                  <p className="text-white font-medium">{data.resumeFile.name}</p>
                  <p className="text-gray-400 text-sm">{formatFileSize(data.resumeFile.size)}</p>
                </div>
              </div>
              <button
                onClick={removeFile}
                className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                title="Remove file"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mt-4 p-3 bg-green-900 bg-opacity-30 border border-green-500 border-opacity-30 rounded-lg">
              <p className="text-green-300 text-sm">
                ✓ Resume uploaded successfully! Elena will analyze this to better understand your experience.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-3 bg-red-900 bg-opacity-50 border border-red-500 rounded-lg text-red-200 text-sm">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-6 space-y-3">
          <button
            onClick={nextStep}
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-200"
          >
            Continue
          </button>
          
          {isOptional && (
            <button
              onClick={skipStep}
              className="w-full px-6 py-3 bg-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-600 transition-all duration-200"
            >
              Skip for now
            </button>
          )}
        </div>

        {/* Benefits */}
        <div className="mt-8 space-y-3">
          <h4 className="text-white font-medium text-center">Why upload your resume?</h4>
          <div className="space-y-2 text-sm text-gray-400">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              AI-powered resume analysis and optimization suggestions
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Better job matching based on your experience
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Personalized application insights and tips
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResumeUploadStep; 